import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonGroup, Pagination } from 'react-bootstrap';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import AccountAccessRequestModal from 'components/modules/user/AccountAccessRequestModal';
import DataPoller from 'components/ui/DataPoller';
import { LocaleContext } from 'providers/locale-settings-context';
import UserWebAPIUtils from '../../../../utils/UserWebAPIUtils';
import SessionStore from '../../../../stores/SessionStore';
import CargomaticBadge from '../../../v2/Badge';
const ACCESS_REQUEST_STATUSES = {
    approved: {
        color: 'green',
        label: 'APPROVED',
    },
    declined: {
        color: 'red',
        label: 'DECLINED',
    },
    pending: {
        color: 'blue',
        label: 'PENDING',
    },
};
const CARRIER_ACCOUNT_ACCESS_GROUPS = {
    approved: {
        action: 'Disable',
        status: 'approved',
        type: 'grantor',
        color: 'blue',
        label: 'Approved',
        localizationSlug: 'APPROVED',
    },
    declined: {
        action: 'Approve',
        status: 'declined',
        type: 'grantor',
        color: 'red',
        label: 'Declined',
        localizationSlug: 'DECLINED',
    },
    requested: {
        action: 'Grant/Deny',
        status: 'pending',
        type: 'grantor',
        color: 'blue',
        label: 'Requested',
        localizationSlug: 'REQUESTED',
    },
    granted: {
        action: 'Decline',
        status: 'approved',
        type: 'grantee',
        color: 'green',
        label: 'Granted',
        localizationSlug: 'GRANTED',
    },
};
const PAGINATION_PER_PAGE_DEFAULT = 10;
const CarrierAccountAccess = ({ carrier, perPage = PAGINATION_PER_PAGE_DEFAULT, }) => {
    const { t } = useTranslation();
    const { formatDate } = useContext(LocaleContext);
    const [accessRequests, setAccessRequests] = useState([]);
    const [activePage, setActivePage] = useState(1);
    const [displayedRequests, setDisplayedRequests] = useState([]);
    const [numberPendingRequested, setPendingNumberRequested] = useState(0);
    const [numberApprovedRequested, setApprovedNumberRequested] = useState(0);
    const [numberGrantedRequested, setGrantedNumberRequested] = useState(0);
    const [numberDeclinedRequested, setDeclinedNumberRequested] = useState(0);
    const [selectedAccessGroup, setSelectedAccessGroup] = useState(CARRIER_ACCOUNT_ACCESS_GROUPS.approved);
    const [accountAccessModalData, setAccountAccessModalData] = useState({
        request: null,
        show: false,
    });
    const carrierCenterView = SessionStore.getUser()._id === carrier._id;
    const filterByStatus = (request) => {
        const userId = SessionStore.getUser()._id;
        if (selectedAccessGroup.status === 'pending' &&
            request.status === 'pending') {
            return true;
        }
        if (selectedAccessGroup.status === 'declined' &&
            request.status === 'declined') {
            return true;
        }
        if (selectedAccessGroup.label === 'Approved' &&
            request.grantee &&
            request.grantee._id === userId &&
            request.status === 'approved') {
            return true;
        }
        if (selectedAccessGroup.label === 'Granted' &&
            request.grantor &&
            request.grantor._id === userId &&
            request.status === 'approved') {
            return true;
        }
        return false;
    };
    const paginationItems = carrierCenterView
        ? accessRequests.filter(filterByStatus)
        : accessRequests;
    const getRequests = async (id) => {
        try {
            let { data } = await UserWebAPIUtils.getAccountAccessRequests(id);
            if ((!accessRequests.length && data && data.length) ||
                (data && data.length && !isEqual(data, accessRequests))) {
                setAccessRequests(data);
                const userId = SessionStore.getUser()._id;
                let requests;
                if (carrierCenterView) {
                    requests = data.filter(filterByStatus).slice(0, perPage);
                }
                else {
                    requests = data.slice(0, perPage);
                }
                setDisplayedRequests(requests);
                setPendingNumberRequested(data.filter((req) => req.status === 'pending').length);
                setApprovedNumberRequested(data.filter((req) => req.status === 'approved' &&
                    req.grantee &&
                    req.grantee._id === userId).length);
                setDeclinedNumberRequested(data.filter((req) => req.status === 'declined').length);
                setGrantedNumberRequested(data.filter((req) => req.status === 'approved' &&
                    req.grantor &&
                    req.grantor._id === userId).length);
            }
        }
        catch (e) {
            console.log(e);
        }
    };
    useEffect(() => {
        getRequests(carrier._id);
    }, []);
    useEffect(() => {
        const filteredRequests = accessRequests
            .filter(filterByStatus)
            .slice(0, perPage);
        setDisplayedRequests(filteredRequests);
    }, [selectedAccessGroup]);
    const onPageSelect = (page) => {
        setActivePage(page);
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;
        setDisplayedRequests(paginationItems.slice(startIndex, endIndex));
    };
    const getAccountAccessNameDisplay = (carrier, grantor, grantor_email, grantee) => {
        if (carrier._id === grantee._id)
            return grantor ? grantor.name : grantor_email;
        return grantee.name;
    };
    const resetAccountAccessModalData = () => setAccountAccessModalData({
        show: false,
        request: null,
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(DataPoller, { frequency: 10000, action: () => getRequests(carrier._id) }),
        carrierCenterView && (React.createElement(ButtonGroup, { className: "grid grid-cols-4 margined-right shipment-btn mb-6" }, Object.values(CARRIER_ACCOUNT_ACCESS_GROUPS).map((accessGroup, accessGroupIndex) => (React.createElement(Button, { key: accessGroup.label + accessGroupIndex.toString(), active: selectedAccessGroup.label === accessGroup.label, onClick: () => setSelectedAccessGroup(accessGroup) },
            React.createElement("span", null, titleize(t(accessGroup.localizationSlug))),
            accessGroup.label === 'Requested' && (React.createElement(CargomaticBadge, { className: "inline-block ml-3", color: "green", size: "small" }, numberPendingRequested)),
            accessGroup.label === 'Declined' && (React.createElement(CargomaticBadge, { className: "inline-block ml-3", color: "green", size: "small" }, numberDeclinedRequested)),
            accessGroup.label === 'Approved' && (React.createElement(CargomaticBadge, { className: "inline-block ml-3", color: "green", size: "small" }, numberApprovedRequested)),
            accessGroup.label === 'Granted' && (React.createElement(CargomaticBadge, { className: "inline-block ml-3", color: "green", size: "small" }, numberGrantedRequested))))))),
        React.createElement("table", { className: "default-table mb-4" },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", null, t('CARRIER', { count: 1 })),
                    React.createElement("th", null, t('STATUS')),
                    carrierCenterView && React.createElement("th", null, t('ACTION')),
                    carrierCenterView && React.createElement("th", null, t('NOTE', { count: 2 })))),
            React.createElement("tbody", null, displayedRequests.map(({ grantee, grantor, grantor_email, status, modified_at }, index) => (React.createElement("tr", { key: grantee + index.toString() },
                React.createElement("td", null, getAccountAccessNameDisplay(carrier, grantor, grantor_email, grantee)),
                React.createElement("td", null,
                    React.createElement("div", { className: "ml-6", style: { textAlign: 'center' } },
                        React.createElement(CargomaticBadge, { color: ACCESS_REQUEST_STATUSES[status].color, size: "large" },
                            ' ',
                            titleize(t('ACCESS')),
                            ' ',
                            status === 'approved' && (grantor === null || grantor === void 0 ? void 0 : grantor._id) === carrier._id
                                ? titleize(t('GRANTED'))
                                : titleize(t(ACCESS_REQUEST_STATUSES[status].label))),
                        modified_at && formatDate(modified_at, DEFAULT_TIMEZONE))),
                carrierCenterView && (React.createElement("td", null, grantor && carrier._id === grantor._id ? (React.createElement(Button, { className: "underline", bsStyle: "link", onClick: () => setAccountAccessModalData((currentData) => {
                        const { show } = currentData;
                        return {
                            show: !show,
                            request: displayedRequests[index],
                        };
                    }) }, t('UPDATE_ACCESS'))) : (React.createElement("div", null, t('N_A'))))),
                carrierCenterView && (React.createElement("td", null, grantor && carrier._id === grantor._id ? (React.createElement("div", null,
                    " ",
                    t('REQUEST_RECEIVED'),
                    " ")) : (React.createElement("div", null,
                    " ",
                    t('REQUEST_SENT'),
                    " "))))))))),
        paginationItems.length > perPage && (React.createElement("div", { className: "flex justify-center" },
            React.createElement(Pagination, { first: true, last: true, ellipsis: true, items: Math.ceil(paginationItems.length / perPage), maxButtons: 10, activePage: activePage, onSelect: (page) => onPageSelect(page) }))),
        accountAccessModalData.show && accountAccessModalData.request && (React.createElement(AccountAccessRequestModal, { handleSingleRequest: true, accessRequest: accountAccessModalData.request, showAccountAccessModal: accountAccessModalData.show, onAccountActionClose: resetAccountAccessModalData }))));
};
CarrierAccountAccess.propTypes = {
    carrier: PropTypes.object.isRequired,
};
export default CarrierAccountAccess;
